import { Alert, Button, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import {
  QuestionsDataSource,
  Question,
  QuestionPayload,
} from '../../../../datasources/questions/QuestionsDataSource';
import { BasketsDataSource } from '../../../../datasources/baskets/BasketsDataSource';
import {
  getApiErrorMessage,
  getGenericErrorMessage,
} from '../../../../datasources/GenericErrors';
import { Basket } from '../../../../types/basket/Basket';
import { BasketListScreen } from '../shared/BasketListScreen';
import { BasketTable, BasketTableColumn } from '../shared/BasketTable';
import { useTextSearch } from '../shared/useTextSearch';
import { CreateQuestionDialog } from './createQuestionDialog';
import { EditQuestionDialog } from './editQuestionDialog';

const OPTION_TYPES: Record<string, string> = {
  SELECT: 'Escolha',
  BOOLEAN: 'Verdadeiro ou falso',
  TEXT: 'Texto livre',
};

const COLUMNS: BasketTableColumn<Question>[] = [
  {
    id: 'question',
    label: 'Pergunta',
    minWidth: 280,
    render: (question) => (
      <>
        <Typography variant="body2" fontWeight={700}>
          {question.text}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {question.code}
        </Typography>
      </>
    ),
  },
  {
    id: 'type',
    label: 'Tipo',
    minWidth: 150,
    render: (question) => OPTION_TYPES[question.type] ?? question.type,
  },
  {
    id: 'options',
    label: 'Opções',
    minWidth: 90,
    render: (question) => question.options?.length ?? 0,
  },
  {
    id: 'required',
    label: 'Obrigatória',
    minWidth: 110,
    render: (question) => (question.required ? 'Sim' : 'Não'),
  },
];

export const QuestionPage = () => {
  const [listError, setListError] = useState<string>();
  const [listRetry, setListRetry] = useState(0);
  const [basketError, setBasketError] = useState<string>();
  const [basketRetry, setBasketRetry] = useState(0);
  const [loadingBaskets, setLoadingBaskets] = useState(true);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState<number>();
  const [createError, setCreateError] = useState<string>();
  const [editError, setEditError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoadingQuestions(true);

    QuestionsDataSource.getAll()
      .then((data) => {
        if (!cancelled) {
          setQuestions(data);
          setListError(undefined);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setListError(
            getGenericErrorMessage(
              error,
              'Não foi possível carregar as perguntas.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingQuestions(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [listRetry]);

  useEffect(() => {
    let cancelled = false;
    setLoadingBaskets(true);

    BasketsDataSource.getAll()
      .then((data) => {
        if (!cancelled) {
          setBaskets(data);
          setBasketError(undefined);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setBasketError(
            getGenericErrorMessage(
              error,
              'Não foi possível carregar os balaios para associação.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBaskets(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [basketRetry]);

  async function handleCreateQuestion(payload: QuestionPayload): Promise<void> {
    setSavingQuestion(true);
    setCreateError(undefined);

    try {
      await QuestionsDataSource.createQuestion(payload);
    } catch (error) {
      setCreateError(
        getApiErrorMessage(error, 'Não foi possível criar a pergunta.')
      );
      setSavingQuestion(false);
      return;
    }

    setCreateDialog(false);
    setSavingQuestion(false);

    try {
      setQuestions(await QuestionsDataSource.getAll());
      setListError(undefined);
    } catch (error) {
      setListError(
        `Pergunta criada, mas ${getGenericErrorMessage(
          error,
          'não foi possível atualizar a lista.'
        )}`
      );
    }
  }

  async function handleUpdateQuestion(
    questionId: number,
    payload: QuestionPayload
  ): Promise<void> {
    setSavingQuestion(true);
    setEditError(undefined);

    try {
      await QuestionsDataSource.updateQuestion(questionId, payload);
    } catch (error) {
      setEditError(
        getApiErrorMessage(error, 'Não foi possível atualizar a pergunta.')
      );
      setSavingQuestion(false);
      return;
    }

    setEditingQuestion(null);
    setSavingQuestion(false);

    try {
      setQuestions(await QuestionsDataSource.getAll());
      setListError(undefined);
    } catch (error) {
      setListError(
        `Pergunta atualizada, mas ${getGenericErrorMessage(
          error,
          'não foi possível atualizar a lista.'
        )}`
      );
    }
  }

  async function handleDeleteQuestion(question: Question): Promise<void> {
    if (
      !window.confirm(
        `Deseja realmente excluir a pergunta "${question.text}"?`
      )
    ) {
      return;
    }

    setDeletingQuestionId(question.id);

    try {
      await QuestionsDataSource.deleteQuestion(question.id);
      setQuestions((currentQuestions) =>
        currentQuestions.filter((item) => item.id !== question.id)
      );
      setListError(undefined);
    } catch (error) {
      setListError(
        getApiErrorMessage(error, 'Não foi possível excluir a pergunta.')
      );
    } finally {
      setDeletingQuestionId(undefined);
    }
  }

  return (
    <>
      <BasketListScreen
        title="Perguntas"
        description="Gerencie as perguntas utilizadas nos fluxos."
        actionLabel="Nova pergunta"
        onAction={() => {
          setCreateError(undefined);
          setCreateDialog(true);
        }}
        searchPlaceholder="Buscar por código ou pergunta..."
        loading={loadingQuestions}
        loadingLabel="Carregando perguntas"
        error={listError}
        onRetry={() => setListRetry((attempt) => attempt + 1)}
        notice={
          basketError && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setBasketRetry((attempt) => attempt + 1)}
                  disabled={loadingBaskets}
                >
                  Tentar novamente
                </Button>
              }
            >
              {basketError}
            </Alert>
          )
        }
      >
        {(search) => (
          <QuestionsTable
            questions={questions}
            search={search}
            deletingQuestionId={deletingQuestionId}
            onEdit={(question) => {
              setEditError(undefined);
              setEditingQuestion(question);
            }}
            onDelete={(question) => void handleDeleteQuestion(question)}
          />
        )}
      </BasketListScreen>

      <CreateQuestionDialog
        open={createDialog}
        questions={questions}
        baskets={baskets}
        basketsLoading={loadingBaskets}
        basketsError={basketError}
        onClose={() => setCreateDialog(false)}
        loading={savingQuestion}
        error={createError}
        onConfirm={handleCreateQuestion}
      />
      {editingQuestion && (
        <EditQuestionDialog
          open={Boolean(editingQuestion)}
          question={editingQuestion}
          questions={questions}
          baskets={baskets}
          basketsLoading={loadingBaskets}
          basketsError={basketError}
          onClose={() => setEditingQuestion(null)}
          loading={savingQuestion}
          error={editError}
          onConfirm={handleUpdateQuestion}
        />
      )}
    </>
  );
};

type QuestionsTableProps = {
  questions: Question[];
  search: string;
  deletingQuestionId?: number;
  onEdit: (question: Question) => void;
  onDelete: (question: Question) => void;
};

const QuestionsTable = ({
  questions,
  search,
  deletingQuestionId,
  onEdit,
  onDelete,
}: QuestionsTableProps) => {
  const rows = useTextSearch(questions, search, (question) => [
    question.text,
    question.code,
    OPTION_TYPES[question.type],
  ]);

  return (
    <BasketTable
      columns={COLUMNS}
      rows={rows}
      getRowId={(question) => question.id}
      getRowLabel={(question) => `pergunta ${question.text}`}
      itemsLabel="perguntas"
      emptyMessage="Nenhuma pergunta cadastrada."
      searching={search.trim() !== ''}
      resetKey={search}
      onEdit={onEdit}
      editLabel="Editar"
      menuActions={[
        {
          label: 'Excluir',
          icon: <DeleteOutline fontSize="small" />,
          destructive: true,
          disabled: (question) => deletingQuestionId === question.id,
          onClick: onDelete,
        },
      ]}
    />
  );
};
